import { assertNever, NormalizedNumber } from '@sparkdotfi/common-universal'
import { ReactNode } from 'react'

import { ActionButton } from './ActionButton'
import { tokenLogos } from '@/config/chain/configs/mainnet/assets'
import {
  SPETH_POINTS_MULTIPLIER,
  SPK_STAKING_POINTS_MULTIPLIER,
  SPUSDC_POINTS_MULTIPLIER,
  SPUSDT_POINTS_MULTIPLIER,
  SUSDS_POINTS_MULTIPLIER,
} from '@/config/consts'
import { formatPercentage } from '@/domain/common/format'
import { USD_MOCK_TOKEN } from '@/domain/types/Token'
import { TokenDefinition } from '@/domain/types/TokenDefinition'
import { EarningsDetails } from '@/features/points/components/points-earnings-panel/EarningsDetails'
import { referralBoostPercentage } from '@/features/points/consts'
import { PointsType } from '@/features/points/types'
import { assets } from '@/ui/assets'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/ui/atoms/accordion/Accordion'
import { Badge } from '@/ui/atoms/badge/Badge'
import { Panel } from '@/ui/atoms/panel/Panel'
import { IconStack } from '@/ui/molecules/icon-stack/IconStack'
import { Info } from '@/ui/molecules/info/Info'
import { TokenAmountTooltip } from '@/ui/organisms/token-amount-tooltip/TokenAmountTooltip'
import { testIds } from '@/ui/utils/testIds'
import { useBreakpoint } from '@/ui/utils/useBreakpoint'

export interface PointsEarningsPanelProps {
  userChainId: number
  invitedUsers: number
  pointsEarnings: {
    type: PointsType
    amount: NormalizedNumber
    campaignActive: boolean
  }[]
  previousPointsSeason: number
  onInvite: () => void
  onBuy: () => void
  isGuestMode: boolean
}

export function PointsEarningsPanel({
  userChainId,
  pointsEarnings,
  invitedUsers,
  previousPointsSeason,
  isGuestMode,
  onInvite,
  onBuy,
}: PointsEarningsPanelProps) {
  return (
    <Panel spacing="m" className="flex flex-col gap-5">
      <Header />
      <Accordion type="multiple" className="grid">
        <div className="typography-label-4 flex items-center justify-between gap-6 border-primary border-b pb-2 text-secondary">
          <div>Task</div>
          <div className="mr-14 hidden items-center justify-between gap-20 md:flex">
            <span>Points earned</span>
            <span>Action</span>
          </div>
          <div className="md:hidden">More info</div>
        </div>
        {pointsEarnings.map((earnings, index) => {
          const metadata = pointsTypeToMetadata(earnings.type, { previousPointsSeason })
          return (
            <AccordionItem
              key={earnings.type}
              value={earnings.type}
              data-testid={testIds.points.earningPanel.row(index)}
            >
              <PointsItem
                userChainId={userChainId}
                type={earnings.type}
                isGuestMode={isGuestMode}
                pointsAmount={earnings.amount}
                campaignActive={earnings.campaignActive}
                previousPointsSeason={previousPointsSeason}
                onInviteClick={onInvite}
                onBuyClick={onBuy}
              />
              <AccordionContent className="mb-6 flex flex-col gap-3 md:gap-5">
                {metadata.description && <div className="typography-body-4 text-secondary">{metadata.description}</div>}
                <div className="flex flex-col gap-3 md:hidden">
                  <div className="flex justify-between">
                    <div className="typography-label-4 text-secondary">Rewards:</div>
                    <div className="typography-label-2">{metadata.reward}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="typography-label-4 text-secondary">Points earned:</div>
                    <PointsAmount value={earnings.amount} />
                  </div>
                </div>
                <EarningsDetails type={earnings.type} invitedUsers={invitedUsers} />
                <ActionButton
                  userChainId={userChainId}
                  type={earnings.type}
                  pointsAmount={earnings.amount}
                  campaignActive={earnings.campaignActive}
                  onInviteClick={onInvite}
                  onBuyClick={onBuy}
                  isGuestMode={isGuestMode}
                  className="md:hidden"
                />
              </AccordionContent>
            </AccordionItem>
          )
        })}
      </Accordion>
    </Panel>
  )
}

interface PointsItemProps {
  userChainId: number
  type: PointsType
  isGuestMode: boolean
  pointsAmount: NormalizedNumber
  campaignActive: boolean
  previousPointsSeason: number
  onInviteClick: () => void
  onBuyClick: () => void
}

function PointsItem({
  userChainId,
  type,
  pointsAmount,
  campaignActive,
  previousPointsSeason,
  isGuestMode,
  onInviteClick,
  onBuyClick,
}: PointsItemProps) {
  const metadata = pointsTypeToMetadata(type, { previousPointsSeason })
  const isDesktop = useBreakpoint('md')
  const MetadataWrapper = campaignActive ? ActiveCampaignBadge : InactiveCampaignBadge

  if (!isDesktop) {
    return (
      <AccordionTrigger>
        <div className="flex items-center gap-2 py-3">
          <IconStack items={metadata.icons} size="base" iconBorder="white" />
          <div className="typography-label-2">{metadata.header}</div>
        </div>
      </AccordionTrigger>
    )
  }

  return (
    <AccordionTrigger>
      <div className="mr-6 grid w-full grid-cols-[6fr_1fr_90px] items-center gap-8 py-5">
        <div className="flex items-center gap-3">
          <IconStack items={metadata.icons} size="base" iconBorder="white" />
          <div className="typography-label-2">{metadata.header}</div>
          <MetadataWrapper>{metadata.reward}</MetadataWrapper>
        </div>
        <PointsAmount value={pointsAmount} />
        <ActionButton
          userChainId={userChainId}
          type={type}
          pointsAmount={pointsAmount}
          campaignActive={campaignActive}
          isGuestMode={isGuestMode}
          onInviteClick={onInviteClick}
          onBuyClick={onBuyClick}
        />
      </div>
    </AccordionTrigger>
  )
}

function PointsAmount({ value }: { value: NormalizedNumber }) {
  return (
    <div className="flex items-center justify-end gap-0.5 md:gap-1.5">
      <img src={assets.points.pointsIcon} alt="Points Icon" className="icon-size-xs" />
      <div className="typography-label-2 text-primary" data-testid={testIds.points.earningPanel.amount}>
        <TokenAmountTooltip token={USD_MOCK_TOKEN} amount={value} options={{ style: 'compact', showSymbol: 'never' }} />
      </div>
    </div>
  )
}

function Header() {
  return <h3 className="typography-heading-5 flex items-baseline gap-1">Start earning points</h3>
}

interface PointsMetadata {
  header: string
  reward: ReactNode
  description: string | undefined
  icons: (string | TokenDefinition)[]
}

function pointsTypeToMetadata(
  pointsType: PointsType,
  { previousPointsSeason }: { previousPointsSeason: number },
): PointsMetadata {
  switch (pointsType) {
    case 'pendle':
      return {
        header: 'Hold USDS YT or LP on Pendle',
        reward: (
          <div className="flex items-center gap-1 text-nowrap">
            <span>25 points</span>
            <Info className="text-brand-primary">Get 25 points per YT/LP per day</Info>
          </div>
        ),
        description: undefined,
        icons: [assets.points.pendleIcon, tokenLogos.usds],
      }
    case 'referral':
      return {
        header: 'Referral boost',
        reward: (
          <div className="flex items-center gap-1 text-nowrap">
            <span>{formatPercentage(referralBoostPercentage, { minimumFractionDigits: 0 })} of SP</span>
          </div>
        ),
        description: `Earn ${formatPercentage(referralBoostPercentage, { minimumFractionDigits: 0 })} of the points your invitees earn.`,
        icons: [tokenLogos.spk, assets.points.inviteIcon],
      }
    case 'staking':
      return {
        header: 'Stake SPK',
        reward: (
          <div className="flex items-center gap-1 text-nowrap">
            <span>{formatPointsWithUnit(SPK_STAKING_POINTS_MULTIPLIER)}</span>
            <Info className="text-brand-primary">
              Get {formatPointsWithUnit(SPK_STAKING_POINTS_MULTIPLIER)} per staked SPK per day
            </Info>
          </div>
        ),
        description: `Earn ${formatPointsWithUnit(SPK_STAKING_POINTS_MULTIPLIER)} per staked SPK per day.`,
        icons: [tokenLogos.spk, assets.points.stakingIcon],
      }
    case 'hold-susdc':
      return {
        header: 'Hold sUSDC',
        reward: (
          <div className="ml-1 flex items-center gap-1 text-nowrap">
            Inactive
            <Info className="text-warning">Campaign was deactivated on January 12, 2026 12:00:00 UTC.</Info>
          </div>
        ),
        description: 'Deposit your USDC into sUSDC and start earning 2 points per day for each USDC deposited.',
        icons: [tokenLogos.susdc, assets.points.holdSusdcIcon],
      }
    case 'hold-susds':
      return {
        header: 'Hold Savings USDS',
        reward: (
          <div className="flex items-center gap-1 text-nowrap">
            <span>{formatPointsWithUnit(SUSDS_POINTS_MULTIPLIER)}</span>
            <Info className="text-brand-primary">
              Get {formatPointsWithUnit(SUSDS_POINTS_MULTIPLIER)} per USDS per day on multiple chains.
            </Info>
          </div>
        ),
        description: `Deposit your USDS into sUSDS and start earning ${formatPointsWithUnit(SUSDS_POINTS_MULTIPLIER)} per day for each USDS deposited.`,
        icons: [tokenLogos.susds, assets.points.holdSusdcIcon],
      }
    case 'hold-spusdc':
      return {
        header: 'Hold Savings USDC',
        reward: (
          <div className="flex items-center gap-1 text-nowrap">
            <span>{formatPointsWithUnit(SPUSDC_POINTS_MULTIPLIER)}</span>
            <Info className="text-brand-primary">
              Get {formatPointsWithUnit(SPUSDC_POINTS_MULTIPLIER)} per USDC per day.
            </Info>
          </div>
        ),
        description: `Deposit your USDC into spUSDC and start earning ${formatPointsWithUnit(SPUSDC_POINTS_MULTIPLIER)} per day for each USDC deposited.`,
        icons: [tokenLogos.spusdc, assets.points.holdSusdcIcon],
      }
    case 'hold-spusdt':
      return {
        header: 'Hold Savings USDT',
        reward: (
          <div className="flex items-center gap-1 text-nowrap">
            <span>{formatPointsWithUnit(SPUSDT_POINTS_MULTIPLIER)}</span>
            <Info className="text-brand-primary">
              Get {formatPointsWithUnit(SPUSDT_POINTS_MULTIPLIER)} per USDT per day.
            </Info>
          </div>
        ),
        description: `Deposit your USDT into spUSDT and start earning ${formatPointsWithUnit(SPUSDT_POINTS_MULTIPLIER)} per day for each USDT deposited.`,
        icons: [tokenLogos.spusdt, assets.points.holdSusdcIcon],
      }
    case 'hold-speth':
      return {
        header: 'Hold Savings ETH',
        reward: (
          <div className="flex items-center gap-1 text-nowrap">
            <span>{formatPointsWithUnit(SPETH_POINTS_MULTIPLIER)}</span>
            <Info className="text-brand-primary">
              Get {formatPointsWithUnit(SPETH_POINTS_MULTIPLIER)} per ETH per day.
            </Info>
          </div>
        ),
        description: `Deposit your ETH into spETH and start earning ${formatPointsWithUnit(SPETH_POINTS_MULTIPLIER)} per day for each ETH deposited.`,
        icons: [tokenLogos.speth, assets.points.holdSusdcIcon],
      }
    case 'governance':
      return {
        header: 'Delegate SPK',
        reward: (
          <div className="flex items-center gap-1 text-nowrap">
            <span>0.5 points</span>
            <Info className="text-brand-primary">Get 0.5 points per SPK/stSPK delegated per day.</Info>
          </div>
        ),
        description: 'Delegate your SPK to earn 0.5 points per day.',
        icons: [tokenLogos.spk, assets.points.governanceIcon],
      }
    case 'loyalty':
      return {
        header: 'Loyalty',
        reward: (
          <div className="flex items-center gap-1 text-nowrap">
            <span>10% of SP in S{previousPointsSeason}</span>
          </div>
        ),
        description: `Claim SPK and stake SPK to get stSPK - stake within 24 hours after you claim SPK in Season ${previousPointsSeason}.`,
        icons: [tokenLogos.spk, assets.points.loyaltyIcon],
      }
    default:
      assertNever(pointsType)
  }
}

function ActiveCampaignBadge({ children }: { children: ReactNode }) {
  return (
    <Badge variant="brand" size="sm" appearance="soft">
      Get {children}
    </Badge>
  )
}

function InactiveCampaignBadge({ children }: { children: ReactNode }) {
  return (
    <Badge variant="warning" size="sm" appearance="soft">
      {children}
    </Badge>
  )
}

const pointsNumberFormatter = new Intl.NumberFormat('en-US')

function formatPoints(points: number): string {
  return pointsNumberFormatter.format(points)
}

function formatPointsWithUnit(points: number): string {
  const unit = points === 1 ? 'point' : 'points'
  return `${formatPoints(points)} ${unit}`
}
